import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useProject } from "../../context/ProjectContext";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle, XCircle, Clock, Eye, MessageSquare, Loader2, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminReviews = () => {
  const { getAuthHeaders } = useAuth();
  const { currentProject } = useProject();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [reviewComment, setReviewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [publishingAll, setPublishingAll] = useState(false);

  const fetchPendingReviews = async () => {
    setLoading(true);
    try {
      const params = currentProject ? `?project_id=${currentProject.id}` : "";
      const response = await axios.get(`${API}/reviews/pending${params}`, { headers: getAuthHeaders() });
      setArticles(response.data);
    } catch (error) {
      console.error("Failed to fetch pending reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject]);

  const handleReview = async (action) => {
    if (!selectedArticle) return;
    setSubmitting(true);

    try {
      await axios.post(
        `${API}/reviews/${selectedArticle.id}`,
        { action, comment: reviewComment },
        { headers: getAuthHeaders() }
      );
      toast.success(`Article ${action === "approve" ? "approved" : "declined"}`);
      setSelectedArticle(null);
      setReviewComment("");
      await fetchPendingReviews();
    } catch (error) {
      toast.error("Failed to process review");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublishAll = async () => {
    if (articles.length === 0) return;
    const scope = currentProject ? `all ${articles.length} pending article(s) for ${currentProject.name}` : `all ${articles.length} pending article(s) across every project`;
    if (!window.confirm(`Publish ${scope}? They will go live on the public site immediately.`)) return;
    setPublishingAll(true);
    try {
      const params = currentProject ? `?project_id=${currentProject.id}` : "";
      const response = await axios.post(`${API}/reviews/publish-all${params}`, {}, { headers: getAuthHeaders() });
      toast.success(response.data?.message || "Published");
      await fetchPendingReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish all");
    } finally {
      setPublishingAll(false);
    }
  };

  return (
    <AdminLayout title="Editor's Review Queue">
      <div className="space-y-6" data-testid="admin-reviews">
        {/* Stats */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-[#64748B]">
            <Clock className="h-4 w-4" />
            <span>{articles.length} articles pending review</span>
          </div>
          <Button
            onClick={handlePublishAll}
            disabled={publishingAll || articles.length === 0}
            data-testid="publish-all-pending-button"
          >
            {publishingAll ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
            Publish all pending
          </Button>
        </div>

        {/* Review Queue */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Card key={i} className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <Skeleton className="h-24 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : articles.length > 0 ? (
          <div className="space-y-4">
            {articles.map((article) => (
              <Card key={article.id} className="border-0 shadow-sm" data-testid={`review-card-${article.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-6">
                    {article.image_url && (
                      <img
                        src={article.image_url}
                        alt=""
                        className="w-32 h-24 object-cover rounded-lg flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <h3 className="font-semibold text-[#0F172A] text-lg">{article.title}</h3>
                        <Badge variant="outline" className="capitalize flex-shrink-0">
                          {article.source}
                        </Badge>
                      </div>
                      <p className="text-[#64748B] text-sm mb-3 line-clamp-2">
                        {article.summary || article.content?.substring(0, 200)}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-[#64748B] mb-4">
                        <span className="capitalize">{article.category}</span>
                        <span>•</span>
                        <span>{article.author_name}</span>
                        <span>•</span>
                        <span>{new Date(article.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedArticle(article)}
                          data-testid={`review-article-${article.id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Review
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12 text-center">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
              <h3 className="font-semibold text-[#0F172A] mb-2">All caught up!</h3>
              <p className="text-[#64748B]">No articles pending review</p>
            </CardContent>
          </Card>
        )}

        {/* Review Dialog */}
        <Dialog open={!!selectedArticle} onOpenChange={(open) => !open && setSelectedArticle(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Review Article</DialogTitle>
            </DialogHeader>
            {selectedArticle && (
              <div className="space-y-6">
                {selectedArticle.image_url && (
                  <img
                    src={selectedArticle.image_url}
                    alt=""
                    className="w-full h-64 object-cover rounded-lg"
                  />
                )}
                <div>
                  <h2 className="text-2xl font-bold text-[#0F172A] mb-2">{selectedArticle.title}</h2>
                  <div className="flex items-center gap-4 text-sm text-[#64748B]">
                    <Badge variant="outline" className="capitalize">{selectedArticle.category}</Badge>
                    <span>{selectedArticle.author_name}</span>
                    <span>{new Date(selectedArticle.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                {selectedArticle.summary && (
                  <div className="p-4 bg-[#F8FAFC] rounded-lg">
                    <p className="text-[#64748B] italic">{selectedArticle.summary}</p>
                  </div>
                )}
                <div className="prose max-w-none">
                  <p className="text-[#0F172A] whitespace-pre-wrap">{selectedArticle.content}</p>
                </div>
                {selectedArticle.tags?.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {selectedArticle.tags.map((tag) => (
                      <Badge key={tag} variant="secondary">{tag}</Badge>
                    ))}
                  </div>
                )}
                <div className="border-t pt-6">
                  <div className="space-y-2 mb-4">
                    <label className="text-sm font-medium text-[#0F172A]">
                      <MessageSquare className="h-4 w-4 inline mr-2" />
                      Review Comment (optional)
                    </label>
                    <Textarea
                      value={reviewComment}
                      onChange={(e) => setReviewComment(e.target.value)}
                      placeholder="Add feedback for the author..."
                      rows={3}
                      data-testid="review-comment-input"
                    />
                  </div>
                  <div className="flex items-center justify-end gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setSelectedArticle(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleReview("decline")}
                      disabled={submitting}
                      data-testid="decline-article-button"
                    >
                      {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      <XCircle className="h-4 w-4 mr-2" />
                      Decline
                    </Button>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleReview("approve")}
                      disabled={submitting}
                      data-testid="approve-article-button"
                    >
                      {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve & Publish
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminReviews;
